"use client";

import { DataTable } from "@/components/tool-kit/primitives";
import {
  type GraphMark,
  LinesGraph,
} from "@/components/tools/algebra/lines-graph";
import {
  StepList,
  type WorkedStep,
} from "@/components/tools/algebra/step-list";
import type { EXPONENTIAL_GROWTH_CALCULATOR_STRINGS } from "@/components/tools/exponential-growth-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  amountAt,
  type GrowthInput,
  type GrowthResult,
  growthSchedule,
} from "@/lib/tool-kit/algebra/growth";

type Strings = typeof EXPONENTIAL_GROWTH_CALCULATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

export function amountText(fmt: Format, value: number): string {
  if (value !== 0 && Math.abs(value) < 1) {
    return fmt(value, { maximumSignificantDigits: 4 });
  }
  return fmt(value, { maximumFractionDigits: 4 });
}

export function percentText(fmt: Format, fraction: number): string {
  return `${fmt(fraction * 100, { maximumFractionDigits: 4 })}%`;
}

const signed = (fmt: Format, value: number) =>
  value < 0
    ? `(${fmt(value, { maximumFractionDigits: 6 })})`
    : fmt(value, { maximumFractionDigits: 6 });

/** The formula, then the same formula with this problem's numbers in. */
export function WorkingSteps({
  input,
  result,
  strings,
}: {
  input: GrowthInput;
  result: GrowthResult;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const a = (v: number) => amountText(fmt, v);
  const P = a(result.initial);
  const A = a(result.final);
  const T = a(result.time);
  const discrete = result.model === "discrete";
  const r = fmt(Math.abs(result.rate), { maximumFractionDigits: 6 });
  const sign = result.rate < 0 ? "−" : "+";
  const factor = fmt(result.factor, { maximumFractionDigits: 6 });
  const k = signed(fmt, result.continuousRate);
  const ratio = fmt(result.final / result.initial, {
    maximumFractionDigits: 6,
  });
  const logRatio = fmt(Math.log(result.final / result.initial), {
    maximumFractionDigits: 6,
  });
  const rateText = discrete
    ? fmt(result.rate, { maximumFractionDigits: 6 })
    : k;
  const lines: Record<GrowthInput["solve"], string> = discrete
    ? {
        final: `A = ${P} × (1 ${sign} ${r})^${T} = ${P} × ${fmt(result.factor ** result.time, { maximumFractionDigits: 6 })} = ${A}`,
        initial: `P = ${A} / (1 ${sign} ${r})^${T} = ${A} / ${fmt(result.factor ** result.time, { maximumFractionDigits: 6 })} = ${P}`,
        rate: `r = (${A} / ${P})^(1/${T}) − 1 = ${ratio}^(1/${T}) − 1 = ${rateText}`,
        time: `t = ln(${A} / ${P}) / ln(1 ${sign} ${r}) = ${logRatio} / ${fmt(Math.log(result.factor), { maximumFractionDigits: 6 })} = ${T}`,
      }
    : {
        final: `A = ${P} × e^(${k} × ${T}) = ${P} × e^${fmt(result.continuousRate * result.time, { maximumFractionDigits: 6 })} = ${A}`,
        initial: `P = ${A} / e^(${k} × ${T}) = ${A} / ${fmt(Math.exp(result.continuousRate * result.time), { maximumFractionDigits: 6 })} = ${P}`,
        rate: `r = ln(${A} / ${P}) / ${T} = ${logRatio} / ${T} = ${k}`,
        time: `t = ln(${A} / ${P}) / ${k} = ${logRatio} / ${k} = ${T}`,
      };
  const formula = strings.formulas[result.model][input.solve];
  const steps: WorkedStep[] = [
    { text: strings.stepFormula, math: formula },
    { text: strings.stepSubstitute, math: lines[input.solve] },
  ];
  if (discrete) {
    steps.push({
      text: t(strings.stepFactor, { factor }),
      math: `1 ${sign} ${r} = ${factor}`,
    });
  }
  return <StepList steps={steps} />;
}

export function RulesTable({
  result,
  strings,
  unit,
}: {
  result: GrowthResult;
  strings: Strings;
  unit: string;
}) {
  const fmt = useToolNumber();
  if (result.rules.length === 0) {
    return null;
  }
  return (
    <DataTable
      head={[strings.method, t(strings.timeIn, { unit }), strings.difference]}
      rows={result.rules.map((row) => [
        strings.rules[row.rule],
        fmt(row.time, { maximumFractionDigits: 4 }),
        row.rule === "exact"
          ? "—"
          : `${fmt(row.errorPercent, { maximumFractionDigits: 2, signDisplay: "exceptZero" })}%`,
      ])}
    />
  );
}

export function ScheduleTable({
  result,
  strings,
  unit,
}: {
  result: GrowthResult;
  strings: Strings;
  unit: string;
}) {
  const fmt = useToolNumber();
  const rows = growthSchedule(result);
  return (
    <DataTable
      head={[t(strings.timeIn, { unit }), strings.amount, strings.change]}
      rows={rows.map((row) => [
        fmt(row.time, { maximumFractionDigits: 4 }),
        amountText(fmt, row.amount),
        `${fmt(row.changePercent, { maximumFractionDigits: 2, signDisplay: "exceptZero" })}%`,
      ])}
    />
  );
}

export function GrowthChart({
  result,
  strings,
}: {
  result: GrowthResult;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const horizon = Math.max(result.time, 1);
  const top = Math.max(result.initial, result.final, 1e-9);
  const bottom = Math.min(result.initial, result.final);
  const span = top - bottom || top;
  const point = (x: number, y: number): GraphMark => ({
    x,
    y,
    label: t(strings.point, {
      x: fmt(x, { maximumFractionDigits: 2 }),
      y: amountText(fmt, y),
    }),
  });
  const marks = [point(0, result.initial), point(result.time, result.final)];
  const half = result.characteristic;
  if (half && half.time < horizon) {
    const factor = half.kind === "doubling" ? 2 : 0.5;
    marks.push(point(half.time, result.initial * factor));
  }
  return (
    <LinesGraph
      downloadLabel={strings.downloadPng}
      fileName="exponential-growth-graph.png"
      lines={[{ fn: (x) => amountAt(result, x), label: strings.curveLabel }]}
      marks={marks}
      title={strings.graphTitle}
      viewport={{
        xMin: -horizon * 0.04,
        xMax: horizon * 1.08,
        yMin: Math.min(0, bottom) - span * 0.06,
        yMax: top + span * 0.12,
      }}
    />
  );
}
